import type { AdminAccountRole, AdminPermissions, AdminUserRecord } from '@business-game/shared';
import apiClient from './client';

export type AdminUserStatus = 'active' | 'pending' | 'disabled';

/**
 * `status` / `hasEmail` optionnels côté front tant que le backend ne les expose pas partout.
 * hasEmail === false → l'email stocké est un placeholder à ne jamais afficher.
 */
export type AdminAuthUser = AdminUserRecord & { status?: AdminUserStatus; hasEmail?: boolean };

export type CreateAdminUserPayload = {
  /** Optionnel à la création : le backend utilise l'email par défaut. */
  login?: string;
  /** Optionnel : sans email, le backend dérive un login Prénom.Nom. */
  email?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  /** Absent → compte en attente, le backend renvoie un setupToken. */
  password?: string;
  logoUrl?: string | null;
  role?: AdminAccountRole;
  permissions?: AdminPermissions;
  facilitatorName?: string | null;
  assignedSessionIds?: number[];
};

export type UpdateAdminUserPayload = Partial<CreateAdminUserPayload> & {
  isActive?: boolean;
  parentAdminUserId?: number | null;
};

export const authApi = {
  async login(login: string, password: string): Promise<{ token: string; user: AdminAuthUser }> {
    const response = await apiClient.post('/auth/admin/login', { login, password });
    return response.data.data;
  },

  async me(): Promise<AdminAuthUser> {
    const response = await apiClient.get('/auth/admin/me');
    return response.data.data;
  },

  async updateProfile(data: UpdateAdminUserPayload): Promise<AdminAuthUser> {
    const response = await apiClient.put('/auth/admin/profile', data);
    return response.data.data;
  },

  async getUsers(): Promise<AdminAuthUser[]> {
    const response = await apiClient.get('/auth/admin/users');
    return response.data.data;
  },

  async createUser(data: CreateAdminUserPayload): Promise<{ user: AdminAuthUser; setupToken?: string }> {
    const response = await apiClient.post('/auth/admin/users', data);
    return response.data.data;
  },

  async updateUser(id: number, data: UpdateAdminUserPayload): Promise<AdminAuthUser> {
    const response = await apiClient.put(`/auth/admin/users/${id}`, data);
    return response.data.data;
  },

  /** Invalide le mot de passe actuel et renvoie un nouveau lien d'accès. */
  async resetLink(id: number): Promise<{ user: AdminAuthUser; setupToken: string }> {
    const response = await apiClient.post(`/auth/admin/users/${id}/reset-link`);
    return response.data.data;
  },

  async getPasswordSetup(token: string): Promise<{ displayName: string; email: string; isFirstSetup: boolean }> {
    const response = await apiClient.get(`/auth/admin/password-setup/${encodeURIComponent(token)}`);
    return response.data.data;
  },

  async completePasswordSetup(token: string, password: string): Promise<{ token: string; user: AdminAuthUser }> {
    const response = await apiClient.post('/auth/admin/password-setup', { token, password });
    return response.data.data;
  },

  async loginAs(id: number): Promise<{ token: string; user: AdminAuthUser }> {
    const response = await apiClient.post(`/auth/admin/users/${id}/login-as`);
    return response.data.data;
  },

  async stopImpersonation(): Promise<{ token: string; user: AdminAuthUser }> {
    const response = await apiClient.post('/auth/admin/stop-impersonation');
    return response.data.data;
  },
};
